import { ReactNode, useState } from "react";
import { useSubscription } from "@/hooks/useSubscription";
import { useAdmin } from "@/hooks/useAdmin";
import { Loader2, Lock, CreditCard, Clock, Check, Star, Building2, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { cn } from "@/lib/utils";
import { useSidebar } from "@/contexts/SidebarContext";
import { usePaddleCheckout } from "@/hooks/usePaddleCheckout";

interface SubscriptionGuardProps {
  children: ReactNode;
  product?: string;
  productLabel?: string;
  monthlyPrice?: number;
}

interface PlanOption {
  id: string;
  label: string;
  months: number;
  discount: number;
  priceId: string;
}

const plans: PlanOption[] = [
  { id: "monthly", label: "Mensal", months: 1, discount: 0, priceId: "audits_bpf_mensal" },
  { id: "semiannual", label: "Semestral", months: 6, discount: 15, priceId: "audits_bpf_semestral" },
  { id: "annual", label: "Anual", months: 12, discount: 25, priceId: "audits_bpf_anual" },
];

export function SubscriptionGuard({
  children,
  product = "audits_bpf",
  productLabel = "Audits_BPF",
  monthlyPrice = 449,
}: SubscriptionGuardProps) {
  const { status, daysRemaining, isLoading, slotsUsed, slotsLimit, slotsRemaining, slotsAtLimit, planType } = useSubscription(product);
  const { isAdmin, isLoading: isAdminLoading } = useAdmin();
  const [selectedPlan, setSelectedPlan] = useState("semiannual");
  const { width: sidebarWidth } = useSidebar();
  const { openCheckout, loading: checkoutLoading } = usePaddleCheckout();

  if (isLoading || isAdminLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (isAdmin) {
    return <>{children}</>;
  }

  const planLabel = planType === "single" ? "Plano Único" : "Trial";

  const SlotsBadge = (
    <div className={cn(
      "flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium",
      slotsAtLimit ? "bg-destructive/15 text-destructive" : "bg-background/50 text-foreground"
    )}>
      <Building2 className="w-3.5 h-3.5" />
      <span>Empresas: {slotsUsed}/{slotsLimit}</span>
      {slotsAtLimit && <AlertTriangle className="w-3.5 h-3.5" />}
    </div>
  );

  if (status === "active") {
    return (
      <>
        {slotsAtLimit && (
          <Alert
            variant="destructive"
            style={{ marginLeft: sidebarWidth }}
            className="rounded-none border-x-0 border-t-0 transition-all duration-300"
          >
            <AlertTriangle className="w-4 h-4" />
            <AlertDescription className="flex items-center justify-between gap-2">
              <span>
                Você atingiu o limite do plano <strong>{planLabel}</strong>: {slotsUsed} de {slotsLimit} empresa{slotsLimit !== 1 ? "s" : ""} cadastrada{slotsLimit !== 1 ? "s" : ""}.
                {planType !== "single" && " Assine um plano para cadastrar mais empresas."}
              </span>
              <Badge variant="outline" className="shrink-0">{slotsUsed}/{slotsLimit}</Badge>
            </AlertDescription>
          </Alert>
        )}
        {children}
      </>
    );
  }

  if (status === "trial") {
    return (
      <div className="relative">
        <div
          style={{ marginLeft: sidebarWidth }}
          className="sticky top-0 z-50 bg-gradient-to-r from-accent via-emerald-500 to-accent text-accent-foreground py-2.5 px-6 font-bold text-xs uppercase tracking-widest flex flex-wrap items-center justify-center gap-4 transition-all duration-300 shadow-lg backdrop-blur-md"
        >
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4" />
            <span>Trial: {daysRemaining} dia{daysRemaining !== 1 ? "s" : ""} restante{daysRemaining !== 1 ? "s" : ""}</span>
            <Progress value={((7 - daysRemaining) / 7) * 100} className="w-24 h-2" />
          </div>
          {SlotsBadge}
        </div>
        {slotsAtLimit && (
          <Alert
            variant="destructive"
            style={{ marginLeft: sidebarWidth }}
            className="rounded-none border-x-0 border-t-0 transition-all duration-300"
          >
            <AlertTriangle className="w-4 h-4" />
            <AlertDescription>
              Limite de empresas do trial atingido ({slotsUsed}/{slotsLimit}). Assine um plano para cadastrar mais empresas.
            </AlertDescription>
          </Alert>
        )}
        {children}
      </div>
    );
  }

  const getPrice = (plan: PlanOption) => {
    const discounted = monthlyPrice * (1 - plan.discount / 100);
    return Math.round(discounted);
  };

  const getTotal = (plan: PlanOption) => {
    return getPrice(plan) * plan.months;
  };

  const selected = plans.find((p) => p.id === selectedPlan)!;

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="max-w-3xl w-full space-y-6">
        <div className="text-center space-y-3">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-destructive/10">
            <Lock className="w-8 h-8 text-destructive" />
          </div>
          <h2 className="text-2xl font-bold text-foreground">Período de teste encerrado</h2>
          <p className="text-muted-foreground">
            Seu trial de 7 dias para o <strong>{productLabel}</strong> expirou. Escolha um plano para continuar.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {plans.map((plan) => {
            const isSelected = selectedPlan === plan.id;
            const pricePerMonth = getPrice(plan);
            const total = getTotal(plan);
            const isBest = plan.id === "semiannual";

            return (
              <Card
                key={plan.id}
                className={cn(
                  "relative cursor-pointer transition-all duration-200 hover:shadow-md",
                  isSelected
                    ? "border-primary ring-2 ring-primary/20"
                    : "border-border hover:border-primary/40"
                )}
                onClick={() => setSelectedPlan(plan.id)}
              >
                {isBest && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <Badge className="gap-1 bg-primary text-primary-foreground">
                      <Star className="w-3 h-3" /> Mais popular
                    </Badge>
                  </div>
                )}
                <CardHeader className="text-center pb-2 pt-5">
                  <CardTitle className="text-lg">{plan.label}</CardTitle>
                  {plan.discount > 0 && (
                    <Badge variant="secondary" className="mx-auto text-xs">
                      {plan.discount}% de desconto
                    </Badge>
                  )}
                </CardHeader>
                <CardContent className="text-center space-y-3">
                  <div>
                    <p className="text-3xl font-bold text-foreground">
                      R$ {pricePerMonth}
                      <span className="text-sm font-normal text-muted-foreground">/mês</span>
                    </p>
                    {plan.months > 1 && (
                      <p className="text-sm text-muted-foreground mt-1">
                        Total: R$ {total.toLocaleString("pt-BR")}
                      </p>
                    )}
                  </div>
                  <ul className="text-sm text-muted-foreground space-y-1 text-left">
                    <li className="flex items-center gap-2"><Check className="w-4 h-4 text-primary shrink-0" /> Acesso completo por {plan.months * 30} dias</li>
                    <li className="flex items-center gap-2"><Check className="w-4 h-4 text-primary shrink-0" /> Suporte prioritário</li>
                    <li className="flex items-center gap-2"><Check className="w-4 h-4 text-primary shrink-0" /> Todas as funcionalidades</li>
                  </ul>
                </CardContent>
              </Card>
            );
          })}
        </div>

        <div className="text-center space-y-3">
          <Button
            size="lg"
            className="gap-2 px-8"
            disabled={checkoutLoading}
            onClick={() => openCheckout(selected.priceId)}
          >
            <CreditCard className="w-5 h-5" />
            {checkoutLoading ? "Abrindo..." : `Assinar ${selected.label} — R$ ${getTotal(selected).toLocaleString("pt-BR")}`}
          </Button>
          <p className="text-xs text-muted-foreground">
            Pagamento seguro • Cartão de crédito • Imposto e nota fiscal cobertos pelo provedor
          </p>
        </div>
      </div>
    </div>
  );
}
